import type { Value } from 'platejs';
import { deserializeMd } from '@platejs/markdown';
import remarkGfm from 'remark-gfm';

// @platejs/markdown returns nested ul/li/lic trees for lists, but our editor
// uses the v53 BlockList indent-based model (top-level <p> blocks with
// listStyleType + indent props). Flatten the tree here so commit's
// insertFragment / insertNodes lands the AI output in the same shape the
// rest of the editor produces — otherwise list nodes render as plain text
// because the editor has no <ul>/<li>/<lic> element renderers registered.
const LIST_TYPE_MAP: Record<string, 'disc' | 'decimal'> = { ul: 'disc', ol: 'decimal' };

function flattenLists(nodes: any[], indent = 0): any[] {
    const out: any[] = [];
    for (const node of nodes) {
        if (node && (node.type === 'ul' || node.type === 'ol')) {
            const listStyleType = LIST_TYPE_MAP[node.type];
            for (const li of node.children ?? []) {
                if (li?.type !== 'li') continue;
                let textChildren: any[] = [];
                const nestedLists: any[] = [];
                for (const child of li.children ?? []) {
                    if (child?.type === 'lic' || child?.type === 'p') {
                        textChildren = textChildren.concat(child.children ?? []);
                    } else if (child?.type === 'ul' || child?.type === 'ol') {
                        nestedLists.push(child);
                    } else {
                        textChildren.push(child);
                    }
                }
                if (textChildren.length === 0) textChildren = [{ text: '' }];
                out.push({ type: 'p', listStyleType, indent: indent + 1, children: textChildren });
                if (nestedLists.length > 0) out.push(...flattenLists(nestedLists, indent + 1));
            }
        } else if (node && Array.isArray(node.children)) {
            out.push({ ...node, children: flattenLists(node.children, indent) });
        } else {
            out.push(node);
        }
    }
    return out;
}

// Convert an AI-streamed markdown string to a Plate node tree shaped for our
// editor. Uses @platejs/markdown's MDAST-based deserializer with the GFM
// extension (tables / strikethrough / autolinks), then post-processes the
// result through flattenLists to match our v53 BlockList model.
//
// Requires an editor instance so deserializeMd can route block-/leaf-level
// nodes through the editor's plugin set (basic-nodes for headings/marks,
// TablePlugin for table sub-trees, LinkPlugin for `a` etc.).
export function markdownToPlate(editor: any, md: string): Value {
    if (!md) return [{ type: 'p', children: [{ text: '' }] }] as Value;
    let nodes: any[];
    try {
        nodes = deserializeMd(editor, md, { remarkPlugins: [remarkGfm] }) as any[];
    } catch {
        return [{ type: 'p', children: [{ text: md }] }] as Value;
    }
    const flat = flattenLists(nodes ?? []);
    if (flat.length === 0) return [{ type: 'p', children: [{ text: '' }] }] as Value;
    return flat as Value;
}
